import { midnightIsoParam as midnightIso } from '@/shared/filters/dates';
import { msg } from '@lingui/core/macro';

import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';

import { CONFIRMATION_TRANSACTION_TYPE, HAS_BILLING_DATE, hasBillingDateParam } from './wallet.enums';
import type { WalletOption } from './wallet.options';

// Filtros das listas de Carteiras, declarados uma vez (P01). Leve: entra no `validateSearch` da rota;
// a busca de carteira vem da porta leve (`wallet.options.ts`) por import dinâmico.

const api = () => import('./wallet.options');
const walletLabel = (item: WalletOption) => `(${String(item.walletCode)}) - ${item.beneficiaryName ?? ''}`;

/**
 * "Código convênio" (`multiple: true`, `WalletsListContainer.tsx:89`): o legado acumulava códigos
 * digitados; aqui a pessoa escolhe pelo código ou pelo titular (mesma escolha das réguas). O fio é o
 * mesmo: `walletsCode` repetido.
 */
export const walletCodeFilterSource = defineEntitySource<WalletOption>({
  key: ['wallet', 'filter', 'walletsCode'],
  scope: () => getCurrentTenant() ?? null,
  search: async (query, signal) => (await (await api()).listWalletOptions({ search: query || undefined, tenant: getCurrentTenant() }, signal)).data,
  byId: async (id, signal) => {
    const page = await (await api()).listWalletOptions({ search: id, tenant: getCurrentTenant() }, signal);
    return page.data.find((item) => String(item.walletCode) === id);
  },
  toOption: (item) => ({ value: String(item.walletCode), label: walletLabel(item) }),
});

/** Lista de carteiras (`WalletsListContainer.tsx:83-92`, `WalletFilters.tsx:12-24`). */
export const walletListFilters = defineFilters([
  { key: 'searchString', type: 'search', label: msg`Todos`, placeholder: msg`Filtrar por todos` },
  { key: 'walletsCode', type: 'entity', multiple: true, label: msg`Código convênio`, placeholder: msg`Código ou titular do convênio`, source: walletCodeFilterSource },
  { key: 'name', type: 'text', label: msg`Titular`, placeholder: msg`Filtrar por titular` },
  { key: 'alias', type: 'text', label: msg`Nome da carteira`, placeholder: msg`Filtrar por nome da carteira` },
  { key: 'bankAccount', type: 'text', label: msg`Conta`, placeholder: msg`Filtrar por conta` },
]);

/** Meia-noite de São Paulo em ISO UTC: `new Date(<DatePicker>).toISOString()` do legado (`filterContext.tsx:92-99, 132-134`). */
export { midnightIsoParam as midnightIso } from '@/shared/filters/dates';

/** Arquivos remessa: "Data" (`FileInstructionListContainer.tsx:139-149`); início e fim com o fuso (padrão). */
export const instructionFileFilters = defineFilters([
  { key: 'initialDate', to: 'finalDate', type: 'dateRange', label: msg`Data`, fromLabel: msg`Data inicial`, toLabel: msg`Data final`, pinned: true },
]);

/**
 * Arquivos retorno: o mesmo período, com `useDefaultFinalDateFormat: true` (`FileConfirmationListContainer.tsx:124-130`):
 * o fim vai como `toISOString()` do dia escolhido, não como fim do dia.
 */
export const confirmationFileFilters = defineFilters([
  { key: 'initialDate', to: 'finalDate', type: 'dateRange', label: msg`Data`, fromLabel: msg`Data inicial`, toLabel: msg`Data final`, pinned: true, toParam: midnightIso },
]);

/**
 * Tarifas (`CostsListContainer.tsx:65-95`): o início não se chama `initialDate`, então vai como
 * `toISOString()` (`filterContext.tsx:256-258`); o fim, com `formatFinalDateWithTimeZone` (padrão).
 */
export const costsFilters = defineFilters([
  { key: 'initialBillingDate', to: 'finalBillingDate', type: 'dateRange', label: msg`Data de cobrança`, fromLabel: msg`Data inicial de cobrança`, toLabel: msg`Data final de cobrança`, fromParam: midnightIso },
  { key: 'initialCreationDate', to: 'finalCreationDate', type: 'dateRange', label: msg`Data de lançamento`, fromLabel: msg`Data inicial de lançamento`, toLabel: msg`Data final de lançamento`, fromParam: midnightIso },
  { key: 'confirmationTransactionType', type: 'select', label: msg`Tipo de instrução`, options: CONFIRMATION_TRANSACTION_TYPE },
  { key: 'hasBillingDate', type: 'select', label: msg`Ver tarifas cobradas`, options: HAS_BILLING_DATE, toParam: hasBillingDateParam },
]);
